import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Chip } from '../ModuleFrame';
import { apiPost } from '../../utils/backendApi';
import { STUDIO_JOURNEY } from '../../data/consoleModules';
import { askNova } from '../ConsoleHeader';
import ScreenReview from './ScreenReview';
import { ACTIVE, ActiveBar, Shot, ago, tellNova } from './shared';

// Concept phase — the studio's board ([[studio-journey]]). A concept is a
// labArtifact kind 'concept' (spec + narration) drafted by the concept run
// from the interview transcript + research. Eight figures, one URL each
// (?fig=), so the voice agent presents it one figure per turn with
// navigate_app; ?present=1 is the full-bleed version; ?screen=<id> focuses
// one screen on the Screens figure (ScreenReview); ?revise=1 opens the
// revision panel. Accept / Revise / Draft / Render visuals go through /lab
// (labApi.js) — the same routes the accept_concept / revise_concept /
// render_visuals composers use.
//
// Asking for a change: Revise is available at ANY status (an accepted
// concept gets a new proposed version to review). The revision panel is a
// round of notes — each aimed at the figure or screen on view ("Screens ›
// Needs Attention: …"), kept per concept in localStorage until sent — plus
// a general remark; one Revise sends the whole round as the feedback.
// "Request a change" on a focused screen opens the panel aimed at it.

export const FIGS = [
  { id: 'problem', label: 'Problem & promise' },
  { id: 'personas', label: 'People' },
  { id: 'landscape', label: 'Landscape' },
  { id: 'features', label: 'Features' },
  { id: 'screens', label: 'Screens' },
  { id: 'flows', label: 'Flows' },
  { id: 'visuals', label: 'Visuals' },
  { id: 'questions', label: 'Open questions' },
];
const CELL = { yes: '●', partial: '◐', no: '○', unknown: '·' };
const PRIO = { must: 'Must have', should: 'Should have', could: 'Could have' };

const narrationText = (art) => FIGS.map((f) => `${f.id}: ${art?.narration?.[f.id] || f.label}`).join(' | ');

// Revision notes for one concept, kept in the browser until the round is
// sent (the artifact is server-written; a note is not worth a round trip).
function useRevisionNotes(conceptId) {
  const key = conceptId ? `nv:concept-notes:${conceptId}` : null;
  const [notes, setNotes] = useState([]);
  useEffect(() => {
    if (!key) { setNotes([]); return; }
    try { setNotes(JSON.parse(window.localStorage.getItem(key) || '[]')); } catch { setNotes([]); }
  }, [key]);
  const save = (next) => {
    setNotes(next);
    if (!key) return;
    try { if (next.length) window.localStorage.setItem(key, JSON.stringify(next)); else window.localStorage.removeItem(key); } catch { /* storage full or blocked — notes live in memory */ }
  };
  return {
    notes,
    add: (n) => save([...notes, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, ...n }]),
    remove: (id) => save(notes.filter((n) => n.id !== id)),
    clear: () => save([]),
  };
}

// The feedback one revision round sends: every note with where it points,
// then the general remark.
export function roundFeedback(notes, general) {
  const lines = notes.map((n) => `- ${n.label}: ${n.text}`);
  if (general?.trim()) lines.push(`${lines.length ? '- Overall: ' : ''}${general.trim()}`);
  return lines.join('\n');
}

function RevisionPanel({ notes, target, onAdd, onRemove, onRevise, nextVersion, busy, activeRun, onClose }) {
  const [text, setText] = useState('');
  const canAdd = text.trim().length >= 3;
  const canSend = (notes.notes.length > 0 || text.trim().length >= 8) && !busy && !activeRun;
  const add = () => { if (!canAdd) return; onAdd({ label: target.label, fig: target.fig, screenId: target.screenId || null, text: text.trim() }); setText(''); };
  return (
    <div className="lab-compose cb-revise" role="group" aria-label="Revision round">
      <div className="lab-card-row" style={{ alignItems: 'baseline' }}>
        <strong>Ask for changes</strong>
        <span className="mf-muted cb-small">{notes.notes.length ? `${notes.notes.length} note${notes.notes.length === 1 ? '' : 's'} in this round` : 'collect notes figure by figure, then revise once'}</span>
        <span style={{ flex: 1 }} />
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onClose}>Close</button>
      </div>
      {notes.notes.length > 0 && (
        <ul className="cb-notes">
          {notes.notes.map((n) => (
            <li key={n.id}>
              <Chip tone={n.screenId ? 'info' : 'muted'}>{n.label}</Chip>
              <span className="cb-note-text">{n.text}</span>
              <button type="button" className="cs-btn-link mf-muted" onClick={() => onRemove(n.id)} aria-label={`Remove note on ${n.label}`}>×</button>
            </li>
          ))}
        </ul>
      )}
      <textarea className="cs-input lab-brief" rows={3} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) add(); }} placeholder={target.screenId ? `What should change on the “${target.name}” screen? e.g. “Make the flagged list the first thing, drop the calculation note.”` : `What should change? e.g. “Drop the marketplace, make the dashboard the first screen, add a weekly digest for managers.”`} aria-label="Revision feedback" />
      <div className="lab-compose-row">
        <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={!canAdd} onClick={add} title="Add this as a note aimed at what is on screen (⌘↵)">+ Note on {target.label}</button>
        <span className="cs-help">Drafts v{nextVersion} from this version, the conversation and the research. Counts as a research run.</span>
        <span style={{ flex: 1 }} />
        <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={!canSend} onClick={() => onRevise(roundFeedback(notes.notes, text))} data-nova-note="Sends every note in this round plus the remark as one revision of the concept">{busy ? 'Starting…' : notes.notes.length ? `Revise with ${notes.notes.length} note${notes.notes.length === 1 ? '' : 's'}${text.trim().length >= 8 ? ' + remark' : ''}` : 'Revise'}</button>
      </div>
    </div>
  );
}

function JourneyStrip({ app, concept }) {
  const step = app.journey?.step || 'interview';
  const idx = Math.max(0, STUDIO_JOURNEY.findIndex((s) => s.id === step));
  const cur = STUDIO_JOURNEY[idx];
  const prompt = step === 'interview' ? 'Interview me about this app idea — one question at a time.'
    : step === 'research' ? 'Research the competitors for this app and let me watch.'
      : step === 'concept' ? 'How is the concept draft coming along?'
        : step === 'review' ? `Present the concept${concept ? ` "${concept.title}"` : ''} to me, one figure at a time.`
          : step === 'stories' ? 'Write the user stories from the accepted concept.'
            : step === 'build' ? 'Build the screens from the concept.' : 'What should we do next on this app?';
  return (
    <div className="lab-journey" role="group" aria-label="Studio journey">
      <ol className="lab-journey-steps">
        {STUDIO_JOURNEY.map((s, i) => <li key={s.id} className={i < idx ? 'is-done' : i === idx ? 'is-cur' : ''} title={s.goal}><span className="lab-journey-n">{i < idx ? '✓' : i + 1}</span>{s.label}</li>)}
      </ol>
      <div className="lab-journey-now">
        <span className="mf-muted">{cur?.goal}</span>
        <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => askNova(prompt)}>Continue with NoVA</button>
      </div>
    </div>
  );
}

// Catch-up — the design reads the build ([[design-catches-up-from-the-build]]).
// `dev` is operateFor().dev: Dev Console sessions / turns on the instance
// since the last catch-up, the conversations here, and whether this kit
// knows the export. One press → a concept run with catchup:true; the run
// lands the concept, ideas and stories as proposed, nothing is accepted.
function CatchUpCard({ dev, canEdit, busy, activeRun, onStart }) {
  if (!dev) return null;
  const never = dev.neverCaughtUp;
  const parts = [];
  if (dev.kitKnows) parts.push(`${dev.devSessions || 0} Dev Console session${dev.devSessions === 1 ? '' : 's'} (${dev.devTurns || 0} turns)`);
  parts.push(`${dev.planeTurns || 0} conversation turn${dev.planeTurns === 1 ? '' : 's'} here`);
  const since = never ? 'so far' : `since ${ago(dev.lastCatchupAt)}`;
  const quiet = !dev.behind && !never;
  return (
    <div className={`lab-detail cb-catchup ${dev.behind ? 'is-behind' : ''}`} style={{ padding: 12 }} role="group" aria-label="Catch up from the build">
      <div className="lab-card-row" style={{ alignItems: 'baseline', gap: 8 }}>
        <Chip tone={dev.behind ? 'warn' : never ? 'info' : 'ok'}>{dev.behind ? 'design is behind the build' : never ? 'never caught up' : 'design is current'}</Chip>
        <strong>{parts.join(' · ')} {since}</strong>
        <span style={{ flex: 1 }} />
        {canEdit && <button type="button" className={`cs-btn cs-btn-sm ${dev.behind || never ? 'cs-btn-primary' : 'cs-btn-outline'}`} disabled={!!activeRun || busy === 'catchup'} onClick={onStart} data-nova-note="Reads the instance's Dev Console and the conversations, drafts the concept, lands ideas and stories as proposed">{busy === 'catchup' ? 'Starting…' : 'Catch up from the build'}</button>}
      </div>
      <p className="mf-muted cb-small" style={{ margin: '6px 0 0' }}>
        {quiet ? 'Nothing new to read. ' : ''}
        {dev.kitKnows
          ? 'Reads what was asked, planned, built and shipped in the instance\'s Dev Console plus every conversation about this app here, then drafts the concept and lands ideas and user stories on their boards as proposed — you review, then "Accept everything" or pick.'
          : (dev.lastDevAt || dev.devSessions
            ? 'This instance runs an older kit without the Dev Console export — only the conversations here are read until it is rolled forward.'
            : 'No hosted instance is linked, so only the conversations here are read.')}
        {dev.lastShipAt ? ` Last ship on the instance ${ago(dev.lastShipAt)}.` : ''}
      </p>
    </div>
  );
}

// The result line + Accept everything for a finished catch-up run.
function CatchUpResult({ run, canApply, busy, onAcceptAll, onPhase }) {
  const c = run?.findings?.catchup;
  if (!c) return null;
  const accepted = run.acceptedAll || false;
  return (
    <div className="lab-detail cb-catchup-result" style={{ padding: 12 }} role="group" aria-label="Catch-up result">
      <div className="lab-card-row" style={{ alignItems: 'baseline', gap: 8 }}>
        <Chip tone="ok">caught up</Chip>
        <span>drafted from <strong>{c.devSessions || 0}</strong> Dev Console session{c.devSessions === 1 ? '' : 's'} · <strong>{c.turns || 0}</strong> conversation turns{c.research ? ` · ${c.research} research run${c.research === 1 ? '' : 's'}` : ''}</span>
        <span style={{ flex: 1 }} />
        {canApply && !accepted && <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy === 'acceptAll'} onClick={onAcceptAll} data-nova-note="Accepts the concept, every proposed idea and story from this run, and applies the design proposals">{busy === 'acceptAll' ? 'Accepting…' : 'Accept everything'}</button>}
      </div>
      <p className="mf-muted cb-small" style={{ margin: '6px 0 0' }}>
        <button type="button" className="cs-btn-link" onClick={() => onPhase?.('ideas')}>{c.ideas || 0} idea{c.ideas === 1 ? '' : 's'}</button> and <button type="button" className="cs-btn-link" onClick={() => onPhase?.('stories')}>{c.stories || 0} stor{c.stories === 1 ? 'y' : 'ies'}</button> landed as proposed{c.built ? ` (${c.built} already built on the instance)` : ''}{c.skipped ? `, ${c.skipped} duplicate${c.skipped === 1 ? '' : 's'} skipped` : ''}.
        {c.digest?.kitKnows === false ? ' The instance runs an older kit, so only the conversations here were read.' : ''}
        {' '}Accept the concept here, or everything at once — accepted stories move the journey to Build, where each one has a "Build this" on the instance.
      </p>
    </div>
  );
}

function Fig({ id, c, org, screenSel, setScreenSel, buildScreen = null, requestChange = null, busy = '' }) {
  const s = c.spec || {};
  const narr = c.narration?.[id];
  const personaName = (pid) => s.personas?.find((p) => p.id === pid)?.name || (pid === 'all' ? 'everyone' : pid);
  const screenName = (sid) => s.screens?.find((x) => x.id === sid)?.name || sid;
  const body = (() => {
    switch (id) {
      case 'problem':
        return (
          <div className="cb-problem">
            <div className="cb-hero"><span className="cb-k">Problem</span><p>{s.summary?.problem || '—'}</p></div>
            <div className="cb-hero is-promise"><span className="cb-k">Promise</span><p>{s.summary?.promise || '—'}</p></div>
            {s.summary?.whyNow && <div className="cb-hero"><span className="cb-k">Why now</span><p>{s.summary.whyNow}</p></div>}
            {s.summary?.differentiators?.length ? <div className="cb-hero"><span className="cb-k">What sets it apart</span><ul className="cb-bullets">{s.summary.differentiators.map((d, i) => <li key={i}>{d}</li>)}</ul></div> : null}
          </div>
        );
      case 'personas':
        return (
          <div className="cb-personas">
            {(s.personas || []).map((p) => (
              <div key={p.id} className="cb-persona">
                <div className="cb-avatar" aria-hidden="true">{(p.name || '?').slice(0, 1).toUpperCase()}</div>
                <strong>{p.name}</strong>
                <span className="mf-muted">{p.who}</span>
                <dl className="lab-kv"><dt>Wants</dt><dd>{p.goal}</dd><dt>Struggles with</dt><dd>{p.pain}</dd></dl>
                <span className="mf-muted cb-small">{(s.features || []).filter((f) => f.solves === p.id).length} features aimed here</span>
              </div>
            ))}
            {!(s.personas || []).length && <p className="mf-muted">No personas yet.</p>}
          </div>
        );
      case 'landscape': {
        const m = s.matrix || {};
        return (
          <div className="cb-landscape">
            {m.criteria?.length ? (
              <div className="lab-table-wrap">
                <table className="lab-table cb-matrix">
                  <thead><tr><th>Who</th>{m.criteria.map((k) => <th key={k}>{k}</th>)}</tr></thead>
                  <tbody>{(m.rows || []).map((r) => <tr key={r.name} className={/^this app$/i.test(r.name) ? 'is-cur' : ''}><td>{r.name}</td>{r.cells.map((v, i) => <td key={i} className={`cb-cell is-${v}`} title={v}>{CELL[v] || '·'}</td>)}</tr>)}</tbody>
                </table>
                <p className="mf-muted cb-small">● yes · ◐ partial · ○ no · · unknown</p>
              </div>
            ) : <p className="mf-muted">No comparison yet — research first.</p>}
            <div className="cb-competitors">
              {(s.competitors || []).map((x) => (
                <div key={x.name} className="cb-competitor">
                  <strong>{x.url ? <a href={x.url} target="_blank" rel="noreferrer">{x.name}</a> : x.name}</strong>
                  {x.pricing && <Chip>{x.pricing}</Chip>}
                  <span className="mf-muted">{x.positioning}</span>
                  {x.strengths?.length ? <span className="cb-small"><b>+</b> {x.strengths.join(' · ')}</span> : null}
                  {x.weaknesses?.length ? <span className="cb-small"><b>−</b> {x.weaknesses.join(' · ')}</span> : null}
                </div>
              ))}
            </div>
          </div>
        );
      }
      case 'features':
        return (
          <div className="cb-features">
            {['must', 'should', 'could'].map((p) => (
              <section key={p} className={`lab-col cb-prio is-${p}`}>
                <h4 className="lab-h">{PRIO[p]} <span className="mf-muted">{(s.features || []).filter((f) => f.priority === p).length}</span></h4>
                {(s.features || []).filter((f) => f.priority === p).map((f) => (
                  <div key={f.id} className="lab-card cb-feature">
                    <strong>{f.name} {f.differentiator && <Chip tone="ok">differentiator</Chip>}</strong>
                    <span className="mf-muted lab-card-sub">{f.what}</span>
                    <span className="mf-muted cb-small">for {personaName(f.solves)}</span>
                  </div>
                ))}
              </section>
            ))}
          </div>
        );
      case 'screens':
        return <ScreenReview org={org} concept={c} selected={screenSel} onSelect={setScreenSel} onBuild={buildScreen} onRequestChange={requestChange} busy={busy} />;
      case 'flows':
        return (
          <div className="cb-flows">
            {(s.flows || []).map((f) => (
              <div key={f.id} className="cb-flow">
                <strong>{f.name} <span className="mf-muted">· {personaName(f.persona)}</span></strong>
                <ol className="cb-flow-steps">
                  {f.steps.map((st, i) => (
                    <li key={i}><button type="button" className="cb-flow-screen" onClick={() => setScreenSel(st.screenId)} title="Show this screen">{screenName(st.screenId)}</button><span>{st.action}</span>{i < f.steps.length - 1 && <span className="cb-arrow" aria-hidden="true">→</span>}</li>
                  ))}
                </ol>
              </div>
            ))}
            {!(s.flows || []).length && <p className="mf-muted">No flows yet.</p>}
          </div>
        );
      case 'visuals': {
        const imgs = s.visuals || [];
        return imgs.length ? (
          <div className="cb-gallery">
            {imgs.map((v, i) => (
              <figure key={v.key || i} className="cb-visual">
                <Shot org={org} runId={v.runId} k={v.key} large auto alt={v.screenId ? `Concept image of ${screenName(v.screenId)}` : 'Concept image'} />
                <figcaption className="mf-muted">{v.screenId ? screenName(v.screenId) : v.title || 'Concept'}{v.prompt ? <span className="cb-small"> — {v.prompt}</span> : null}</figcaption>
              </figure>
            ))}
          </div>
        ) : <p className="mf-muted">No concept images yet — “Render visuals” draws the key screens with an image model (counts toward Lab images).</p>;
      }
      case 'questions':
        return (s.openQuestions || []).length ? <ol className="cb-questions">{s.openQuestions.map((q, i) => <li key={i}>{q}</li>)}</ol> : <p className="mf-muted">Nothing open.</p>;
      default: return null;
    }
  })();
  return (
    <div className={`cb-fig cb-fig-${id}`}>
      {narr && <p className="cb-narr">{narr}</p>}
      {body}
    </div>
  );
}

export default function Concept({ org, app, artifacts, runs, info, sel, setSel, fig, setFig, screen = null, setScreen, revise = false, setRevise, present, setPresent, canEdit, canApply, canBuild = false, busy, act, onPhase, onTab, dev = null, reloadOps }) {
  const concepts = useMemo(() => artifacts.filter((a) => a.kind === 'concept').sort((a, b) => (b.version || 0) - (a.version || 0)), [artifacts]);
  const concept = concepts.find((a) => a.id === sel) || concepts.find((a) => a.id === app.journey?.conceptId) || concepts[0] || null;
  const activeRun = runs.find((r) => (r.kind === 'concept' || r.kind === 'visuals') && ACTIVE.has(r.status)) || null;
  // The catch-up run that produced the concept on screen (if it was one).
  const catchupRun = concept ? runs.find((r) => r.kind === 'concept' && r.catchup && r.status === 'done' && r.findings?.conceptId === concept.id) || null : null;
  const curFig = FIGS.find((f) => f.id === fig) || FIGS[0];
  const figIdx = FIGS.indexOf(curFig);
  const notes = useRevisionNotes(concept?.id);
  // Where a new note points: the focused screen on the Screens figure, else
  // the figure on view.
  const focusedScreen = curFig.id === 'screens' && screen ? (concept?.spec?.screens || []).find((s) => s.id === screen) || null : null;
  const noteTarget = focusedScreen
    ? { label: `Screens › ${focusedScreen.name}`, fig: 'screens', screenId: focusedScreen.id, name: focusedScreen.name }
    : { label: curFig.label, fig: curFig.id, screenId: null, name: curFig.label };

  // Tell the live voice session the board is on screen — once per concept.
  // A concept drafted so an image could render already has a visuals run;
  // say that, and do not leave NoVA asking for a second yes.
  const toldRef = useRef('');
  const renderToldRef = useRef('');
  useEffect(() => {
    if (!concept) return;
    const chained = runs.some((r) => (r.kind === 'concept' && r.thenRender && r.findings?.conceptId === concept.id)
      || (r.kind === 'visuals' && r.conceptId === concept.id));
    const url = `builder?app=${app.id}&tab=lab&phase=concept&id=${concept.id}&present=1&fig=`;
    const renderLine = chained
      ? ' An image render already started from this draft — say that it is rendering, in one sentence. Do not ask whether to render.'
      : '';
    const cu = runs.find((r) => r.kind === 'concept' && r.catchup && r.findings?.conceptId === concept.id)?.findings?.catchup;
    const catchupLine = cu
      ? ` This concept CAUGHT UP from the build: it read ${cu.devSessions || 0} Dev Console session(s) and ${cu.turns || 0} conversation turns, and landed ${cu.ideas || 0} idea(s) and ${cu.stories || 0} user stor(y/ies) as proposed on their boards${cu.built ? ` (${cu.built} already built on the instance)` : ''}. Say that first, in one sentence, and that "Accept everything" on the board takes all of it at once.`
      : '';
    if (toldRef.current !== concept.id) {
      toldRef.current = concept.id;
      if (chained) renderToldRef.current = concept.id;
      tellNova(`CONCEPT BOARD READY — "${concept.title}" v${concept.version} for the app "${app.name}" is on screen (status ${concept.status}).${catchupLine} Present it ONE figure per turn: say what the figure shows in two or three sentences and one recommendation, then ask what the user thinks. Figures, in order, with your notes: ${narrationText(concept)}. Show a figure with navigate_app "${url}<figId>". Revise with "revise the concept: <feedback>" through the copilot; accept with "accept the concept".${renderLine}`, { respond: concept.status === 'proposed' });
      return;
    }
    if (chained && renderToldRef.current !== concept.id) {
      renderToldRef.current = concept.id;
      tellNova('LAB RUN — an image render started from the concept that just finished. Say that it is rendering. Do not ask for a yes.', { respond: true });
    }
  }, [concept, runs, app.id, app.name]);

  // A finished concept run → select the fresh version; finished visuals →
  // show the gallery and tell NoVA.
  const lastDoneRef = useRef('');
  const visualsToldRef = useRef(new Set());
  useEffect(() => {
    const done = runs.find((r) => r.kind === 'concept' && r.status === 'done' && r.findings?.conceptId);
    if (done && done.id !== lastDoneRef.current) { lastDoneRef.current = done.id; if (done.findings.conceptId !== sel) setSel(done.findings.conceptId); }
    const vis = runs.find((r) => r.kind === 'visuals' && r.status === 'done' && r.finishedAt && Date.now() - r.finishedAt < 120000);
    if (vis && !visualsToldRef.current.has(vis.id)) {
      visualsToldRef.current.add(vis.id);
      setFig('visuals');
      const n = (vis.findings?.images || []).length;
      tellNova(n ? `VISUALS READY — ${n} concept image(s) of ${vis.findings.images.map((i) => `"${i.title}"`).join(', ')} are on the Visuals figure of the concept board (on screen now). Describe the direction in a sentence and ask whether it feels right or what to change.` : `VISUALS FAILED — no images came back (${(vis.findings?.errors || []).map((e) => e.error).join('; ').slice(0, 200)}). Tell the user briefly and move on with the wireframes.`, { respond: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runs]);

  useEffect(() => {
    if (!present) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setPresent(false);
      if (e.key === 'ArrowRight' && figIdx < FIGS.length - 1) setFig(FIGS[figIdx + 1].id);
      if (e.key === 'ArrowLeft' && figIdx > 0) setFig(FIGS[figIdx - 1].id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [present, figIdx, setFig, setPresent]);

  const draft = () => act('draft', async () => { await apiPost(`/lab/${org.id}/runs`, { appId: app.id, kind: 'concept', brief: 'Draft the concept from the interview and research.' }); });
  const sendRevision = (feedback) => act('revise', async () => {
    await apiPost(`/lab/${org.id}/artifacts/${concept.id}/revise`, { feedback });
    notes.clear();
    setRevise(false);
    tellNova(`The user asked for a revision of the concept "${concept.title}" v${concept.version}: ${feedback.slice(0, 600)}. A new version is drafting (a minute or two); say so in a sentence and that the board will show v${(concepts[0]?.version || 0) + 1} to review.`, { respond: true });
  });
  // "Request a change" on a focused screen → the panel, aimed at it.
  const requestChange = (scr) => { if (scr?.id) setScreen(scr.id); setRevise(true); };
  const accept = () => act('accept', async () => { await apiPost(`/lab/${org.id}/artifacts/${concept.id}/accept`, {}); tellNova(`The user accepted the concept "${concept.title}" v${concept.version}. The journey is now at "stories" — offer to write the user stories or carry the concept into Design.`, { respond: true }); });
  const renderVisuals = () => act('visuals', async () => { await apiPost(`/lab/${org.id}/runs`, { appId: app.id, kind: 'visuals', conceptId: concept.id, brief: `Render the key screens of "${concept.title}".` }); setFig('visuals'); });
  const catchUp = () => act('catchup', async () => {
    await apiPost(`/lab/${org.id}/runs`, { appId: app.id, kind: 'concept', catchup: true });
    tellNova('CATCH-UP STARTED — the design is reading the instance\'s Dev Console sessions and the conversations here, then drafting the concept, ideas and stories. Two to three minutes. Say so in a sentence; the board will say when it lands.', { respond: true });
  });
  const acceptAll = () => act('acceptAll', async () => {
    const out = await apiPost(`/lab/${org.id}/runs/${catchupRun.id}/accept-all`, {});
    reloadOps?.();
    tellNova(`The user accepted everything from the catch-up: the concept, ${out?.accepted?.ideas || 0} idea(s), ${out?.accepted?.stories || 0} stor(y/ies)${out?.applied?.length ? ` and ${out.applied.length} design proposal(s)` : ''}. The journey is at Build — each accepted story has a "Build this" on the Stories board; offer to build the first one.`, { respond: true });
  });
  const stale = concept && concepts[0] && concept.id !== concepts[0].id;
  // One concept screen → one small build on the instance (the accepted
  // concept is what the instance holds, so only then).
  const buildScreen = canBuild && app.links?.instanceAppId && concept?.status === 'accepted'
    ? (scr) => act(`build-screen-${scr.id}`, async () => {
      await apiPost(`/apps/${org.id}/${app.id}/sync-instance`, { build: { screenIds: [scr.id] } });
      tellNova(`The user sent the screen "${scr.name}" to the instance's Dev Console as a small build. Say a coding-agent session is building it and will open a pull request; the Dev Console shows progress.`, { respond: true });
    })
    : null;
  const catchupCard = <CatchUpCard dev={dev} canEdit={canEdit} busy={busy} activeRun={activeRun} onStart={catchUp} />;

  if (!concept && !activeRun) {
    return (
      <div className="lab-detail cb-start">
        <JourneyStrip app={app} />
        {catchupCard}
        <h3 style={{ margin: 0 }}>Start with the idea</h3>
        <p className="mf-muted" style={{ margin: 0 }}>NoVA interviews you about the app, researches what exists, and drafts the concept here: problem, people, landscape, features, screens, flows. You talk; she navigates. Or draft straight from what this app already says about itself.</p>
        <div className="lab-compose-row">
          <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => askNova(app.journey?.step ? 'Interview me about this app idea — one question at a time.' : `Start the studio for this app: "${app.goal || app.name}". Interview me about it.`)}>Talk it through with NoVA</button>
          {canEdit && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy === 'draft'} onClick={draft}>{busy === 'draft' ? 'Starting…' : 'Draft the concept now'}</button>}
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onPhase?.('research')}>Research first</button>
        </div>
      </div>
    );
  }

  const board = (
    <>
      <nav className="cb-tabs" aria-label="Concept figures">
        {FIGS.map((f, i) => <button key={f.id} type="button" className={`lab-seg-btn ${f.id === curFig.id ? 'is-on' : ''}`} onClick={() => setFig(f.id)} aria-current={f.id === curFig.id ? 'page' : undefined}><span className="lab-rail-n">{i + 1}</span> {f.label}{f.id === 'visuals' && concept?.spec?.visuals?.length ? ` (${concept.spec.visuals.length})` : ''}</button>)}
      </nav>
      {concept ? <Fig id={curFig.id} c={concept} org={org} screenSel={screen} setScreenSel={setScreen} buildScreen={present ? null : buildScreen} requestChange={canEdit ? requestChange : null} busy={busy} /> : <p className="mf-muted">Drafting…</p>}
    </>
  );
  const revisePanel = revise && concept && canEdit && (
    <RevisionPanel notes={notes} target={noteTarget} onAdd={notes.add} onRemove={notes.remove} onRevise={sendRevision} nextVersion={(concepts[0]?.version || 0) + 1} busy={busy === 'revise'} activeRun={!!activeRun} onClose={() => setRevise(false)} />
  );
  const noteCount = notes.notes.length;

  if (present) {
    return (
      <div className="cb-present" role="dialog" aria-label={`Presenting ${concept?.title || 'concept'}`}>
        <header className="cb-present-head">
          <strong>{concept?.title}</strong> <span className="mf-muted">v{concept?.version} · {curFig.label} · {figIdx + 1}/{FIGS.length}</span>
          <span style={{ flex: 1 }} />
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={figIdx === 0} onClick={() => setFig(FIGS[figIdx - 1].id)}>← Prev</button>
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={figIdx === FIGS.length - 1} onClick={() => setFig(FIGS[figIdx + 1].id)}>Next →</button>
          {canEdit && <button type="button" className={`cs-btn cs-btn-sm ${revise ? 'cs-btn-primary' : 'cs-btn-outline'}`} onClick={() => setRevise(!revise)} aria-pressed={revise} title="Leave a note on this figure; notes are sent together as one revision">Note{noteCount ? ` (${noteCount})` : ''}</button>}
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setPresent(false)}>Exit</button>
        </header>
        <div className="cb-present-body">{revisePanel}{board}</div>
      </div>
    );
  }

  return (
    <>
      <div className="lab-list cb-side">
        <JourneyStrip app={app} concept={concept} />
        {catchupCard}
        {activeRun && <div className="lab-detail" style={{ padding: 10 }}><ActiveBar org={org} run={activeRun} canEdit={canEdit} busy={busy} act={act} etaSec={info?.eta?.[activeRun.kind]} /><p className="mf-muted cb-small" style={{ margin: '6px 0 0' }}>{activeRun.kind === 'visuals' ? 'Rendering concept images…' : activeRun.catchup ? (activeRun.stage || 'Reading the Dev Console on the instance, then drafting the concept, ideas and stories…') : 'Reading the interview and research, drafting the concept…'}</p></div>}
        {concepts.length > 0 && (
          <section className="lab-section">
            <h4 className="lab-h">Versions</h4>
            {concepts.map((c) => (
              <button key={c.id} type="button" className={`lab-card ${c.id === concept?.id ? 'is-on' : ''}`} onClick={() => setSel(c.id)} aria-pressed={c.id === concept?.id}>
                <div className="lab-card-row"><Chip tone={c.status === 'accepted' ? 'ok' : c.status === 'rejected' ? 'muted' : 'info'}>{c.status === 'rejected' ? 'superseded' : c.status}</Chip><strong>v{c.version}</strong><span style={{ flex: 1 }} /><span className="mf-muted lab-card-meta">{ago(c.createdAt)}</span></div>
                <div className="lab-card-brief">{c.title}</div>
                {c.feedback && <span className="mf-muted lab-card-sub lab-clamp2">revised for: {c.feedback}</span>}
              </button>
            ))}
          </section>
        )}
      </div>
      <div className="lab-detail cb-detail">
        {catchupRun && <CatchUpResult run={catchupRun} canApply={canApply} busy={busy} onAcceptAll={acceptAll} onPhase={onPhase} />}
        {concept && (
          <header className="lab-detail-head">
            <div>
              <h3>{concept.title}</h3>
              <span className="mf-muted">v{concept.version} · <Chip tone={concept.status === 'accepted' ? 'ok' : concept.status === 'rejected' ? 'muted' : 'info'}>{concept.status === 'rejected' ? 'superseded' : concept.status}</Chip> · {concept.spec?.screens?.length || 0} screens · {concept.spec?.features?.length || 0} features · {concept.spec?.competitors?.length || 0} competitors{concept.source?.labRunId ? <> · <button type="button" className="cs-btn-link mf-muted" onClick={() => onPhase?.('research', concept.source.labRunId)}>run</button></> : null}</span>
              {stale && <p className="cs-help" style={{ margin: '4px 0 0' }}>A newer version exists — <button type="button" className="cs-btn-link" onClick={() => setSel(concepts[0].id)}>show v{concepts[0].version}</button>.</p>}
            </div>
            <div className="lab-run-actions">
              <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setPresent(true)} title="Full-screen, one figure at a time (← → keys)">Present</button>
              {canEdit && <button type="button" className={`cs-btn cs-btn-sm ${revise ? 'cs-btn-primary' : 'cs-btn-outline'}`} onClick={() => setRevise(!revise)} aria-expanded={revise} title={concept.status === 'accepted' ? 'Ask for changes — a new version is drafted for review; this one stays accepted until you accept the new one' : 'Ask for changes — a new version is drafted from your notes'}>Revise{noteCount ? ` (${noteCount})` : ''}</button>}
              {canEdit && concept.status !== 'accepted' && !!concept.spec?.screens?.length && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={!!activeRun || busy === 'visuals'} onClick={renderVisuals} title={`Draw the key screens with an image model (${info?.usage?.images ?? 0}/${info?.usage?.included?.images ?? 0} Lab images used)`}>Render visuals</button>}
              {canApply && concept.status === 'proposed' && <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy === 'accept'} onClick={accept}>{busy === 'accept' ? 'Accepting…' : 'Accept concept'}</button>}
              {concept.status === 'accepted' && <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => askNova('Write the user stories from the accepted concept.')}>Write the stories</button>}
              {concept.status === 'accepted' && <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onTab?.('design')}>Open Design</button>}
            </div>
          </header>
        )}
        {revisePanel}
        {board}
        {concept?.feedback && <p className="mf-muted cb-small" style={{ margin: 0 }}>This version answers: “{concept.feedback}”</p>}
      </div>
    </>
  );
}
